import { cn } from 'noddi-ui-common';
import { activeLocaleTag } from 'noddi-util';
import { I18nProvider } from 'react-aria-components/I18nProvider';
import { TimeField as AriaTimeField, DateInput, DateSegment, FieldError, Label } from 'react-aria-components/TimeField';
import { NoddiTooltip } from '../Overlay/NoddiTooltip';
import { formatTimeValue, parseTimeValue } from './timeValue';

type Props = Pick<React.AriaAttributes, 'aria-label' | 'aria-labelledby' | 'aria-describedby' | 'aria-invalid'> & {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onBlur?: React.FocusEventHandler<Element>;
  onFocus?: React.FocusEventHandler<Element>;
  onKeyDown?: React.KeyboardEventHandler<Element>;
  id?: string;
  name?: string;
  label?: string;
  tooltip?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  labelClassName?: string;
};

export const NoddiTimeField = ({
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  id,
  name,
  label,
  tooltip,
  min,
  max,
  disabled,
  readOnly,
  required,
  className,
  wrapperClassName,
  labelClassName,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid
}: Props) => (
  // react-aria otherwise takes the browser's language; follow the app's display locale, as dates do.
  <I18nProvider locale={activeLocaleTag()}>
    <AriaTimeField
      id={id}
      name={name}
      value={value === undefined ? undefined : parseTimeValue(value)}
      defaultValue={parseTimeValue(defaultValue)}
      onChange={(nextValue) => onChange?.(nextValue ? formatTimeValue(nextValue) : '')}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      minValue={parseTimeValue(min) ?? undefined}
      maxValue={parseTimeValue(max) ?? undefined}
      hourCycle={24}
      granularity='minute'
      shouldForceLeadingZeros
      isDisabled={disabled}
      isReadOnly={readOnly}
      isRequired={required}
      isInvalid={ariaInvalid === true || ariaInvalid === 'true' || undefined}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      className={cn('flex flex-col gap-0.5', wrapperClassName)}
    >
      {label && (
        <div className='flex items-center gap-1'>
          <Label className={cn('text-sm', labelClassName)}>{label}</Label>
          {tooltip && <NoddiTooltip content={tooltip} label={`More information about ${label}`} />}
        </div>
      )}
      <DateInput
        className={cn(
          'flex h-9 w-full items-center rounded-md border border-input bg-white px-2.5 text-base shadow-xs outline-hidden transition-colors data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[focus-within]:ring-1 data-[focus-within]:ring-ring data-[invalid]:border-destructive md:text-sm',
          className
        )}
      >
        {(segment) => (
          <DateSegment
            segment={segment}
            className={cn(
              'outline-hidden',
              segment.type !== 'literal' &&
                'rounded-sm px-0.5 tabular-nums data-[focused]:bg-primary data-[focused]:text-primary-foreground data-[placeholder]:text-muted-foreground'
            )}
          />
        )}
      </DateInput>
      <FieldError className='text-xs text-destructive' />
    </AriaTimeField>
  </I18nProvider>
);
